import { ArrowUpTrayIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, useDisclosure, Input } from "@nextui-org/react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { isResponseErrorObject } from "../../../../../services/helpers";
import { yupResolver } from "@hookform/resolvers/yup";
import { postFetcher } from "../../../../../services/api";
import { storageImageType } from "../../../../../enums/storageImageType";
import { useState } from "react";
import * as yup from "yup";

// Validation schema
const uploadStorageImageValidation = yup.object().shape({
  documents: yup
    .array()
    .of(
      yup.object().shape({
        documentType: yup.string().required("Document type is required"),
        url: yup.string().url("Must be a valid URL").required("URL is required"),
        originalFileName: yup.string().required("File name is required"),
      })
    )
    .required(),
});

export default function FormAddImage({ data, mutate }) {
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const { isOpen: isOpenConfirmSave, onOpen: onOpenConfirmSave, onOpenChange: onOpenChangeConfirmSave, onClose: onCloseConfirmSave } = useDisclosure();
  const { isOpen: isOpenConfirmClose, onOpen: onOpenConfirmClose, onOpenChange: onOpenChangeConfirmClose, onClose: onCloseConfirmClose } = useDisclosure();

  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false); // State untuk loading

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(uploadStorageImageValidation),
    defaultValue: {
      documents: storageImageType.map((type) => ({
        documentType: type.toUpperCase().replace(/\s+/g, "_").replace(/\//g, "_"),
        url: "",
        originalFileName: "",
      })),
    },
  });

  const watchedValues = watch();

  // Convert storage type to enum format
  const convertToEnumFormat = (type) => {
    return type.toUpperCase().replace(/\s+/g, "_").replace(/\//g, "_");
  };

  // Generate filename from type
  const generateFileName = (type) => {
    return `${type.toLowerCase().replace(/\s+/g, "_").replace(/\//g, "_")}.jpg`;
  };

  // Check if all fields are filled
  const isAllFieldsFilled = () => {
    return storageImageType.every((type) => {
      const enumType = convertToEnumFormat(type);
      const urlFieldName = `url_${enumType}`;
      return formData[urlFieldName];
    });
  };

  // Handle input change
  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    setHasChanges(true);
  };

  // Handle modal close - intercept close attempt
  const handleModalClose = () => {
    if (hasChanges) {
      onOpenConfirmClose();
    } else {
      onCloseModal();
    }
  };

  // Reset form and close modal
  const onCloseModal = () => {
    setFormData({});
    setHasChanges(false);
    setIsSubmittingForm(false); // Reset loading state
    reset();
    onClose();
  };

  // Force close without confirmation
  const forceCloseModal = () => {
    onCloseConfirmClose();
    onCloseModal();
  };

  // Handle form submission
  const handleFormSubmit = () => {
    if (!isAllFieldsFilled()) {
      toast.error("Harap isi semua field URL sebelum menyimpan. Jika menggunakan google drive mohon pastikan link gambar dapat diakses.");
      return;
    }
    onOpenConfirmSave();
  };

  // Submit to API
  async function onSubmitForm() {
    setIsSubmittingForm(true); // Set loading true
    try {
      const documents = storageImageType.map((type) => {
        const enumType = convertToEnumFormat(type);
        return {
          documentType: type,
          url: formData[`url_${enumType}`],
          originalFileName: generateFileName(type),
        };
      });

      const payload = {
        penyimpananId: data?.penyimpananB3?.id,
        documents: documents,
      };

      await postFetcher("/api/penyimpananB3/upload-from-urls", payload);
      mutate();
      toast.success("Upload foto gudang berhasil!");
      onCloseConfirmSave();
      onCloseModal();
    } catch (error) {
      onCloseConfirmSave();
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    } finally {
      setIsSubmittingForm(false); // Set loading false
    }
  }

  return (
    <div>
      <Button size="sm" onPress={onOpen} color="primary" startContent={<ArrowUpTrayIcon className="size-4" />}>
        Upload Foto Gudang
      </Button>

      {/* Main Form Modal */}
      <Modal
        scrollBehavior="inside"
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleModalClose();
          }
        }}
        size="xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Upload Foto Gudang</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <div className="text-sm text-ink-500 mb-2">Harap isi semua field URL untuk setiap kategori dokumentasi gudang.</div>

                  {storageImageType.map((type, index) => {
                    const enumType = convertToEnumFormat(type);
                    const urlFieldName = `url_${enumType}`;

                    return (
                      <div key={index} className="border rounded-lg p-4 bg-surface-2">
                        <h4 className="font-medium mb-3 text-sm">{type}</h4>
                        <div className="w-full">
                          <Input
                            type="url"
                            label="URL Gambar"
                            labelPlacement="outside"
                            placeholder="https://example.com/image.jpg"
                            value={formData[urlFieldName] || ""}
                            onChange={(e) => handleInputChange(urlFieldName, e.target.value)}
                            isRequired
                            variant="faded"
                            description={`Nama file: ${generateFileName(type)}`}
                            isDisabled={isSubmittingForm} // Disable saat loading
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="light"
                  onPress={handleModalClose}
                  isDisabled={isSubmittingForm} // Disable saat loading
                >
                  Batal
                </Button>
                <Button
                  color="primary"
                  onPress={handleFormSubmit}
                  isDisabled={!isAllFieldsFilled() || isSubmittingForm} // Disable saat loading
                  isLoading={isSubmittingForm} // Show loading
                >
                  Simpan
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confirmation Save Modal */}
      <Modal isOpen={isOpenConfirmSave} onOpenChange={onOpenChangeConfirmSave} isDismissable={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="size-6 text-warning" />
                  Konfirmasi Simpan
                </div>
              </ModalHeader>
              <ModalBody>
                <p>Apakah Anda yakin ingin menyimpan semua data foto gudang yang telah diinput?</p>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="light"
                  onPress={onClose}
                  isDisabled={isSubmittingForm} // Disable saat loading
                >
                  Batal
                </Button>
                <Button
                  color="primary"
                  onPress={onSubmitForm}
                  isLoading={isSubmittingForm} // Loading state
                  isDisabled={isSubmittingForm} // Disable saat loading
                >
                  Ya, Simpan
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confirmation Close Modal */}
      <Modal isOpen={isOpenConfirmClose} onOpenChange={onOpenChangeConfirmClose} isDismissable={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="size-6 text-warning" />
                  Konfirmasi Tutup Form
                </div>
              </ModalHeader>
              <ModalBody>
                <p>Data belum tersimpan, data yang telah diinput akan hilang. Apakah anda yakin ingin menutup form ini?</p>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  variant="light"
                  onPress={onClose}
                  isDisabled={isSubmittingForm} // Disable saat loading
                >
                  Tidak, Lanjut Edit
                </Button>
                <Button
                  color="danger"
                  onPress={forceCloseModal}
                  isDisabled={isSubmittingForm} // Disable saat loading
                >
                  Ya, Tutup Form
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
